import React, { useEffect, useState, useCallback } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useApi } from '../../hooks/useApi';
import {
  Eye, Users, TrendingUp, Globe, RefreshCw, MousePointer,
  ArrowUpRight, ArrowDownRight, Minus, BarChart2, Activity
} from 'lucide-react';

const RANGES = [
  { label: '7D', value: '-7d', days: 7 },
  { label: '30D', value: '-30d', days: 30 },
  { label: '90D', value: '-90d', days: 90 },
];

const ROUTE_LABELS = {
  '/': 'Home / Landing',
  '/login': 'Login',
  '/register': 'Register',
  '/dashboard': 'Dashboard',
  '/dashboard/team': 'Team',
  '/dashboard/projects': 'Projects',
  '/dashboard/activity': 'Activity',
  '/dashboard/reports': 'Reports',
  '/dashboard/goals': 'Goals',
  '/dashboard/tasks': 'Tasks',
  '/dashboard/daily-summary': 'Daily Summary',
  '/dashboard/monthly-summary': 'Monthly Summary',
  '/dashboard/brainops': 'BrainOps',
  '/dashboard/ai-search': 'AI Search',
  '/dashboard/settings': 'Settings',
  '/dashboard/email-insights': 'Email Insights',
  '/dashboard/crm/preview': 'CRM Preview',
  '/dashboard/v2': 'Dashboard V2',
  '/dashboard/v1': 'Dashboard V1',
  '/billing': 'Billing',
  '/download': 'Download',
  '/book-demo': 'Book Demo',
  '/contact-us': 'Contact Us',
  '/features': 'Features',
  '/blog': 'Blog',
  '/privacy-policy': 'Privacy Policy',
  '/terms-of-service': 'Terms of Service',
  '/invite': 'Invite',
  '/forgot-password': 'Forgot Password',
};

const routeLabel = (path) => {
  if (!path) return '—';
  const clean = path.split('?')[0];
  return ROUTE_LABELS[clean] || clean;
};

// Quick trend calc — compare first half vs second half of data array
const getTrend = (dataArr = []) => {
  if (!dataArr.length) return { dir: 'flat', pct: 0 };
  const mid = Math.floor(dataArr.length / 2);
  const first = dataArr.slice(0, mid).reduce((a, b) => a + b, 0);
  const second = dataArr.slice(mid).reduce((a, b) => a + b, 0);
  if (first === 0 && second === 0) return { dir: 'flat', pct: 0 };
  if (first === 0) return { dir: 'up', pct: 100 };
  const pct = Math.round(((second - first) / first) * 100);
  return { dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', pct: Math.abs(pct) };
};

const TrendBadge = ({ trend }) => {
  if (trend.dir === 'flat') return <span className="text-xs text-gray-400 flex items-center gap-0.5"><Minus size={11} /> 0%</span>;
  if (trend.dir === 'up') return <span className="text-xs text-emerald-400 flex items-center gap-0.5 font-medium"><ArrowUpRight size={12} />+{trend.pct}%</span>;
  return <span className="text-xs text-red-400 flex items-center gap-0.5 font-medium"><ArrowDownRight size={12} />-{trend.pct}%</span>;
};

const SkeletonBar = ({ isDark, w = 'w-full', h = 'h-4' }) => (
  <div className={`${w} ${h} rounded-md animate-pulse ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} />
);

const StatCard = ({ icon: Icon, label, value, color, trend, loading, isDark }) => (
  <div className={`relative rounded-2xl p-5 border overflow-hidden transition-all duration-200 hover:scale-[1.01] ${isDark ? 'bg-white/5 border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'}`}>
    {/* Glow accent */}
    <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-10 -translate-y-4 translate-x-4" style={{ background: color }} />
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <div className="p-2 rounded-xl" style={{ background: `${color}20` }}>
          <Icon size={16} style={{ color }} />
        </div>
        {trend && !loading && <TrendBadge trend={trend} />}
      </div>
      {loading ? (
        <>
          <SkeletonBar isDark={isDark} w="w-16" h="h-7" />
          <SkeletonBar isDark={isDark} w="w-24 mt-2" h="h-3" />
        </>
      ) : (
        <>
          <div className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{value ?? '—'}</div>
          <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
        </>
      )}
    </div>
  </div>
);

const ChartCard = ({ title, subtitle, children, loading, spinColor, isDark }) => (
  <div className={`rounded-2xl border p-5 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
    <div className="mb-4">
      <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</p>
      {subtitle && <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{subtitle}</p>}
    </div>
    {loading ? (
      <div className="h-[200px] flex flex-col gap-3 justify-end pb-2">
        {[40, 65, 30, 80, 55, 90, 45].map((h, i) => (
          <div key={i} className={`rounded animate-pulse ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} style={{ height: `${h}%`, width: `${100 / 7 - 2}%`, display: 'inline-block', marginRight: '2%' }} />
        ))}
      </div>
    ) : children}
  </div>
);

const PostHogAnalytics = ({ isDark }) => {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('-30d');
  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 10;

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/v1/posthog/analytics?dateFrom=${range}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [api, range]);

  useEffect(() => { fetchAnalytics(); setTablePage(1); }, [fetchAnalytics]);

  const pvTrend = getTrend(data?.pageviews?.data);
  const dauTrend = getTrend(data?.dau?.data);
  const sigTrend = getTrend(data?.signups?.data);

  const base = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, background: 'transparent', sparkline: { enabled: false }, animations: { enabled: true, easing: 'easeinout', speed: 700 }, fontFamily: 'Inter, sans-serif' },
    grid: { borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', strokeDashArray: 3, padding: { left: 0, right: 0 } },
    xaxis: { labels: { style: { colors: isDark ? '#6b7280' : '#9ca3af', fontSize: '10px' }, rotate: -30 }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { colors: isDark ? '#6b7280' : '#9ca3af', fontSize: '10px' } } },
    tooltip: { theme: isDark ? 'dark' : 'light', style: { fontSize: '12px' } },
    dataLabels: { enabled: false },
    legend: { show: false },
  };

  const gradFill = (opacity = isDark ? 0.3 : 0.15) => ({
    type: 'gradient',
    gradient: { shadeIntensity: 1, opacityFrom: opacity, opacityTo: 0, stops: [0, 100] },
  });

  const pageviewOpts = { ...base, chart: { ...base.chart, type: 'area', id: 'pageviews' }, xaxis: { ...base.xaxis, categories: data?.pageviews?.labels || [] }, colors: ['#3b82f6'], fill: gradFill(), stroke: { curve: 'smooth', width: 2.5 } };
  const dauOpts = { ...base, chart: { ...base.chart, type: 'bar', id: 'dau' }, xaxis: { ...base.xaxis, categories: data?.dau?.labels || [] }, colors: ['#10b981'], plotOptions: { bar: { borderRadius: 5, columnWidth: '55%' } }, fill: { type: 'solid' } };
  const signupOpts = { ...base, chart: { ...base.chart, type: 'area', id: 'signups' }, xaxis: { ...base.xaxis, categories: data?.signups?.labels || [] }, colors: ['#a855f7'], fill: gradFill(), stroke: { curve: 'smooth', width: 2.5 } };

  const topPages = data?.topPages || [];
  const maxViews = topPages[0]?.views || 1;
  const totalTablePages = Math.ceil(topPages.length / TABLE_PAGE_SIZE);
  const pagedRoutes = topPages.slice((tablePage - 1) * TABLE_PAGE_SIZE, tablePage * TABLE_PAGE_SIZE);
  const totalViews = topPages.reduce((a, b) => a + b.views, 0);

  const rankColors = ['#f59e0b', '#94a3b8', '#cd7c2f'];

  return (
    <div className="space-y-6 pb-8">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${isDark ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
            <BarChart2 size={18} className="text-blue-500" />
          </div>
          <div>
            <h2 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>PostHog Analytics</h2>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Live product analytics · Powered by PostHog</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center rounded-xl p-1 gap-1 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-100'}`}>
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={`px-3 py-1.5 text-xs rounded-lg font-semibold transition-all duration-150 ${range === r.value ? 'bg-blue-500 !text-white shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className={`p-2 rounded-xl transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400 border border-white/10' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-red-400 text-sm flex items-center gap-2">
          <span className="text-base">⚠️</span> {error}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Pageviews" value={data?.pageviews?.total?.toLocaleString()} color="#3b82f6" trend={pvTrend} loading={loading} isDark={isDark} />
        <StatCard icon={Users} label="Daily Active Users" value={data?.dau?.total?.toLocaleString()} color="#10b981" trend={dauTrend} loading={loading} isDark={isDark} />
        <StatCard icon={TrendingUp} label="Identifications" value={data?.signups?.total?.toLocaleString()} color="#a855f7" trend={sigTrend} loading={loading} isDark={isDark} />
        <StatCard icon={Globe} label="Most Visited Route" value={routeLabel(topPages[0]?.page)} color="#f59e0b" loading={loading} isDark={isDark} />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Pageviews Over Time" subtitle="Total page hits per day" loading={loading} spinColor="#3b82f6" isDark={isDark}>
          <ReactApexChart options={pageviewOpts} series={[{ name: 'Pageviews', data: data?.pageviews?.data || [] }]} type="area" height={200} />
        </ChartCard>
        <ChartCard title="Daily Active Users" subtitle="Unique users per day" loading={loading} spinColor="#10b981" isDark={isDark}>
          <ReactApexChart options={dauOpts} series={[{ name: 'Active Users', data: data?.dau?.data || [] }]} type="bar" height={200} />
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="User Identifications" subtitle="Identified/logged-in users per day" loading={loading} spinColor="#a855f7" isDark={isDark}>
          <ReactApexChart options={signupOpts} series={[{ name: 'Identifications', data: data?.signups?.data || [] }]} type="area" height={200} />
        </ChartCard>

        {/* Mini top-5 sparklines */}
        <div className={`rounded-2xl border p-5 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-4">
            <Activity size={14} className={isDark ? 'text-amber-400' : 'text-amber-500'} />
            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Route Distribution</p>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => <SkeletonBar key={i} isDark={isDark} h="h-8" />)}
            </div>
          ) : topPages.length === 0 ? (
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data yet</p>
          ) : (
            <div className="space-y-3">
              {topPages.slice(0, 6).map(({ page, views, unique_users }, idx) => {
                const pct = Math.round((views / maxViews) * 100);
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs font-bold shrink-0" style={{ color: rankColors[idx] || (isDark ? '#6b7280' : '#9ca3af') }}>
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <span className={`text-xs truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {routeLabel(page)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{unique_users ?? 0}u</span>
                        <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{views}</span>
                      </div>
                    </div>
                    <div className={`h-1.5 rounded-full ${isDark ? 'bg-white/8' : 'bg-gray-100'}`}>
                      <div
                        className="h-1.5 rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: rankColors[idx] || '#6b7280' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Full Top Routes Table */}
      <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
        {/* Table header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <div className="flex items-center gap-2">
            <MousePointer size={15} className={isDark ? 'text-amber-400' : 'text-amber-500'} />
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Top Routes by Page Views</span>
            {topPages.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                {topPages.length} routes
              </span>
            )}
            {topPages.length > 0 && (
              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                · showing {(tablePage - 1) * TABLE_PAGE_SIZE + 1}–{Math.min(tablePage * TABLE_PAGE_SIZE, topPages.length)} of {topPages.length}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-4 space-y-3">
            {[1,2,3,4,5].map(i => <SkeletonBar key={i} isDark={isDark} h="h-10" />)}
          </div>
        ) : topPages.length === 0 ? (
          <div className={`px-6 py-10 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No pageview data yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-xs uppercase tracking-wide ${isDark ? 'text-gray-500 bg-white/3' : 'text-gray-400 bg-gray-50'}`}>
                  <th className="px-6 py-3 text-left w-10">#</th>
                  <th className="px-6 py-3 text-left">Route</th>
                  <th className="px-6 py-3 text-left">Page Name</th>
                  <th className="px-6 py-3 text-right">Views</th>
                  <th className="px-6 py-3 text-right">Unique Users</th>
                  <th className="px-6 py-3 w-36">Share of Traffic</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-opacity-50" style={{ '--divide-color': isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}>
                {pagedRoutes.map(({ page, views, unique_users }, localIdx) => {
                  const globalIdx = (tablePage - 1) * TABLE_PAGE_SIZE + localIdx;
                  const pct = Math.round((views / maxViews) * 100);
                  const sharePct = totalViews ? Math.round((views / totalViews) * 100) : 0;
                  return (
                    <tr
                      key={globalIdx}
                      className={`transition-colors ${isDark ? 'hover:bg-white/5 border-b border-white/5' : 'hover:bg-gray-50/80 border-b border-gray-50'}`}
                    >
                      <td className="px-6 py-3.5">
                        {globalIdx < 3 ? (
                          <span className="text-base">{['🥇','🥈','🥉'][globalIdx]}</span>
                        ) : (
                          <span className={`text-xs font-bold ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>{globalIdx + 1}</span>
                        )}
                      </td>
                      <td className="px-6 py-3.5">
                        <code className={`text-xs px-2 py-1 rounded-lg font-mono ${isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>
                          {page}
                        </code>
                      </td>
                      <td className={`px-6 py-3.5 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {routeLabel(page)}
                      </td>
                      <td className={`px-6 py-3.5 text-right text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {views.toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <span className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {unique_users?.toLocaleString() ?? '—'}
                        </span>
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                            <div
                              className="h-2 rounded-full transition-all duration-700"
                              style={{ width: `${pct}%`, background: globalIdx === 0 ? '#f59e0b' : globalIdx === 1 ? '#94a3b8' : globalIdx === 2 ? '#cd7c2f' : '#6b7280' }}
                            />
                          </div>
                          <span className={`text-xs w-8 text-right shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sharePct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination footer */}
        {!loading && totalTablePages > 1 && (
          <div className={`px-6 py-3 border-t flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Page {tablePage} of {totalTablePages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTablePage(1)}
                disabled={tablePage === 1}
                className={`px-2 py-1 text-xs rounded-lg transition-colors disabled:opacity-30 ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              >
                «
              </button>
              <button
                onClick={() => setTablePage(p => Math.max(1, p - 1))}
                disabled={tablePage === 1}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors disabled:opacity-30 ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              >
                ‹ Prev
              </button>
              {Array.from({ length: totalTablePages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalTablePages || Math.abs(p - tablePage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className={`px-1.5 text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setTablePage(p)}
                      className={`w-7 h-7 text-xs rounded-lg transition-colors font-medium ${
                        tablePage === p
                          ? 'bg-blue-500 !text-white'
                          : isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))}
                disabled={tablePage === totalTablePages}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors disabled:opacity-30 ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              >
                Next ›
              </button>
              <button
                onClick={() => setTablePage(totalTablePages)}
                disabled={tablePage === totalTablePages}
                className={`px-2 py-1 text-xs rounded-lg transition-colors disabled:opacity-30 ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostHogAnalytics;
